import { ActionIcon, Badge, Flex, Grid, Tooltip } from "@mantine/core";
import {
  IconChecklist,
  IconEye,
  IconPaperclip,
  IconMessage,
} from "@tabler/icons-react";
import { useEffect, useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useCheckPerm, useGetApi } from "../../../../../../hooks";
import type { ICheckList } from "../../../../../../interfaces";
import { type ISummaryItem } from "../../../../../../pages/Summary/Summary.types";
import { useDispatch } from "../../../../../../redux/hooks";
import { setCheckListsModalProps } from "../../../../../../redux/slices/generatorSlice";
import { StyledTd } from "../../../../../../styled";
import { getChangedChecklists } from "../../../../../../utils/logbookUtils/getChangedChecklists";
import { Button } from "../../../../../shared/Button/Button";
import HistoricalSnapshotButton from "../../../../HistoricalSnapshot/HistoricalSnapshotButton";
interface Props {
  row: ISummaryItem;
  openFiles: () => void;
  openComments: (item: ISummaryItem) => void;
  red?: boolean;
}
const ActionsCell = ({ row, openFiles, openComments, red }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { aid } = useParams();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const { data: checklistsData, refetch: fetchChecklists } = useGetApi<{
    checklists: ICheckList[];
  }>({
    request: "get-entry-checklist",
    body: { params: { lbeid: row.lbeid, partid: row.partid, meid: row.meid } },
    fetchOnInit: false,
  });
  const parsedChecklists = useMemo(
    () =>
      checklistsData?.checklists && Array.isArray(checklistsData.checklists)
        ? getChangedChecklists(checklistsData.checklists, true, true)
        : [],
    [checklistsData],
  );

  useEffect(() => {
    if (row.lbeid || row.meid || row.partid) void fetchChecklists();
  }, [fetchChecklists, row]);

  const handleOpenChecklistsModal = () => {
    if (!checklistsData) return;
    dispatch(
      setCheckListsModalProps({
        opened: true,
        summaryChecklists: parsedChecklists,
      }),
    );
  };
  const handleView = () => {
    const idType = row.weid
      ? "weid"
      : row.meid
        ? "meid"
        : row.partid
          ? "partid"
          : row.lbeid
            ? "lbeid"
            : "";

    if (!idType) return;

    if (location.pathname.includes("summary/audits/audit")) {
      navigate(
        `/summary/audits/audit/${aid}/${idType}/${row[idType]}${row.preview ? "?preview=true" : ""}`,
      );
      return;
    }

    if (location.pathname.includes("summary/reviews/review")) {
      navigate({
        pathname: `/summary/reviews/review/${aid}/${idType}/${row[idType]}`,
        search: row.preview ? "?preview=true" : "",
      });
      return;
    }

    if (location.pathname.includes("exports") && row.meid) {
      navigate(`/exports/entry/${row.meid}` + location.search);
      return;
    }

    const req = searchParams.get("req");
    navigate({
      pathname: `/summary/verify-entries/${idType}/${row[idType]}`,
      search: req ? `?req=${req}` : "",
    });
  };

  const checkPerm = useCheckPerm();
  return (
    <StyledTd red={red}>
      <Flex gap={5} direction="row" justify="flex-end" align="center">
        <Tooltip withArrow label={`Attachments: ${row.attachments.length}`}>
          <ActionIcon
            p={4}
            size={32}
            disabled={row.attachments.length === 0 || !checkPerm("viewfiles")}
            // variant="transparent"
            variant="outline"
            color="blue"
            onClick={openFiles}
            style={{ position: "relative" }}>
            <IconPaperclip />
            {row.attachments.length > 0 && (
              <Badge
                size={"xs"}
                variant="filled"
                color="blue"
                style={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  pointerEvents: "none",
                }}>
                {row.attachments.length}
              </Badge>
            )}
          </ActionIcon>
        </Tooltip>
        <Tooltip withArrow label={`Checklists: ${parsedChecklists.length}`}>
          <ActionIcon
            p={4}
            size={32}
            disabled={parsedChecklists.length === 0}
            // variant="transparent"
            variant="outline"
            color="blue"
            onClick={handleOpenChecklistsModal}
            style={{ position: "relative" }}>
            <IconChecklist />
            {parsedChecklists.length > 0 && (
              <Badge
                size="xs"
                variant="filled"
                color="blue"
                style={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  pointerEvents: "none",
                }}>
                {parsedChecklists.length}
              </Badge>
            )}
          </ActionIcon>
        </Tooltip>
        <Tooltip
          withArrow
          label={`Comments${
            row.comments && row.comments.length > 0
              ? `: ${row.comments.length}`
              : ""
          }`}>
          <ActionIcon
            p={4}
            size={32}
            variant="outline"
            color="blue"
            onClick={() => openComments(row)}
            style={{ position: "relative" }}>
            <IconMessage />
            {row.comments && row.comments.length > 0 && (
              <Badge
                size="xs"
                variant="filled"
                color="blue"
                style={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  pointerEvents: "none",
                }}>
                {row.comments.length}
              </Badge>
            )}
          </ActionIcon>
        </Tooltip>
        <Tooltip withArrow label="View">
          <ActionIcon
            p={4}
            size={32}
            variant="outline"
            color="blue"
            onClick={handleView}>
            <IconEye />
          </ActionIcon>
        </Tooltip>
        <HistoricalSnapshotButton
          disabled={!row.preview}
          summaryAction
          lbid={row.lbid}
          preview
        />
      </Flex>
    </StyledTd>
  );
};
export default ActionsCell;
